import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { billingRepository } from "../repositories";
const DEFAULT_PAGINATION = {
    limit: 25,
    offset: 0,
    total: 0,
};
export const useBillingStore = defineStore("billing", () => {
    const platformSubscriptions = ref([]);
    const pagination = ref({ ...DEFAULT_PAGINATION });
    const plans = ref([]);
    const selectedSubscription = ref(null);
    const tenantSummary = ref(null);
    const platformInvoices = ref([]);
    const tenantInvoices = ref([]);
    const latestPreview = ref(null);
    const isLoading = ref(false);
    const isLoadingPlans = ref(false);
    const isLoadingDetail = ref(false);
    const isSaving = ref(false);
    const error = ref(null);
    const totalAmountDueMinor = computed(() => platformSubscriptions.value.reduce((sum, item) => sum + (item.amounts.open_invoice_amount_minor ?? 0), 0));
    const expectedMonthlyChargeMinor = computed(() => platformSubscriptions.value.reduce((sum, item) => sum + (item.amounts.expected_monthly_charge_minor ?? 0), 0));
    const readyToInvoiceCount = computed(() => platformSubscriptions.value.filter((item) => item.recommended_action.code === "manual_invoice_ready").length);
    const manualCount = computed(() => platformSubscriptions.value.filter((item) => item.subscription.billing_mode === "manual").length);
    const attentionCount = computed(() => platformSubscriptions.value.filter((item) => ["warning", "critical"].includes(item.recommended_action.severity)).length);
    async function fetchPlatformSubscriptions(query = {}) {
        isLoading.value = true;
        error.value = null;
        try {
            const response = await billingRepository.listSubscriptions(query);
            platformSubscriptions.value = response.items;
            pagination.value = response.pagination;
            if (selectedSubscription.value &&
                !response.items.some((item) => item.organization.id === selectedSubscription.value?.organization.id)) {
                selectedSubscription.value = null;
            }
            return response.items;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Could not load subscriptions";
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    }
    async function fetchPlans() {
        isLoadingPlans.value = true;
        error.value = null;
        try {
            const response = await billingRepository.listPlans();
            plans.value = response.items;
            return response.items;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Could not load billing plans";
            throw err;
        }
        finally {
            isLoadingPlans.value = false;
        }
    }
    async function createPlan(payload) {
        isSaving.value = true;
        error.value = null;
        try {
            const response = await billingRepository.createPlan(payload);
            plans.value = response.items;
            return response.items;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Could not create billing plan";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function updatePlan(planId, payload) {
        isSaving.value = true;
        error.value = null;
        try {
            const response = await billingRepository.updatePlan(planId, payload);
            plans.value = response.items;
            return response.items;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Could not update billing plan";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function createPlanPriceVersion(planId, payload) {
        isSaving.value = true;
        error.value = null;
        try {
            const response = await billingRepository.createPlanPriceVersion(planId, payload);
            plans.value = response.items;
            return response.items;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Could not create price version";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function syncPlanPriceToProvider(planId, priceId, payload) {
        isSaving.value = true;
        error.value = null;
        try {
            await billingRepository.syncPlanPriceToProvider(planId, priceId, payload);
            await fetchPlans();
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Could not sync provider price";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function fetchPlatformDetail(orgId) {
        isLoadingDetail.value = true;
        error.value = null;
        try {
            const subscription = await billingRepository.getPlatformSubscription(orgId);
            selectedSubscription.value = subscription;
            upsertPlatformSubscription(subscription);
            return subscription;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Could not load billing detail";
            throw err;
        }
        finally {
            isLoadingDetail.value = false;
        }
    }
    async function fetchTenantSummary(orgId) {
        isLoading.value = true;
        error.value = null;
        try {
            const summary = await billingRepository.getOrganizationSummary(orgId);
            tenantSummary.value = summary;
            return summary;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Could not load billing summary";
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    }
    async function fetchPlatformInvoices(orgId) {
        isLoadingDetail.value = true;
        error.value = null;
        try {
            const response = await billingRepository.listPlatformInvoices(orgId);
            platformInvoices.value = response.items;
            return response.items;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Could not load invoice history";
            throw err;
        }
        finally {
            isLoadingDetail.value = false;
        }
    }
    async function fetchTenantInvoices(orgId) {
        isLoading.value = true;
        error.value = null;
        try {
            const response = await billingRepository.listTenantInvoices(orgId);
            tenantInvoices.value = response.items;
            return response.items;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Could not load invoice history";
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    }
    async function previewCharge(orgId, payload) {
        isSaving.value = true;
        error.value = null;
        try {
            latestPreview.value = await billingRepository.previewCharge(orgId, payload);
            return latestPreview.value;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Could not preview charge";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function syncSeats(orgId, payload = {}) {
        isSaving.value = true;
        error.value = null;
        try {
            const subscription = await billingRepository.syncSeats(orgId, payload);
            selectedSubscription.value = subscription;
            upsertPlatformSubscription(subscription);
            return subscription;
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : "Could not sync seats";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function updateSubscription(orgId, payload) {
        isSaving.value = true;
        error.value = null;
        try {
            const subscription = await billingRepository.updateSubscription(orgId, payload);
            selectedSubscription.value = subscription;
            upsertPlatformSubscription(subscription);
            return subscription;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Could not update subscription";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function migrateToProvider(orgId, payload) {
        return mutateProviderSubscription(() => billingRepository.migrateToProvider(orgId, payload));
    }
    async function syncProviderSubscription(orgId, payload) {
        return mutateProviderSubscription(() => billingRepository.syncProviderSubscription(orgId, payload));
    }
    async function syncProviderSeats(orgId, payload) {
        return mutateProviderSubscription(() => billingRepository.syncProviderSeats(orgId, payload));
    }
    async function changeProviderPlan(orgId, payload) {
        return mutateProviderSubscription(() => billingRepository.changeProviderPlan(orgId, payload));
    }
    async function cancelProviderSubscription(orgId, payload) {
        return mutateProviderSubscription(() => billingRepository.cancelProviderSubscription(orgId, payload));
    }
    async function createProviderSetupIntent(orgId, payload = {}) {
        isSaving.value = true;
        error.value = null;
        try {
            return await billingRepository.createProviderSetupIntent(orgId, payload);
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Could not start provider setup";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function createManualInvoice(orgId, payload) {
        isSaving.value = true;
        error.value = null;
        try {
            const subscription = await billingRepository.createManualInvoice(orgId, payload);
            selectedSubscription.value = subscription;
            upsertPlatformSubscription(subscription);
            return subscription;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Could not create manual invoice";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function markInvoicePaid(invoiceId, payload) {
        isSaving.value = true;
        error.value = null;
        try {
            const subscription = await billingRepository.markInvoicePaid(invoiceId, payload);
            selectedSubscription.value = subscription;
            upsertPlatformSubscription(subscription);
            return subscription;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Could not mark invoice paid";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function rejectInvoiceProof(invoiceId, payload) {
        isSaving.value = true;
        error.value = null;
        try {
            const response = await billingRepository.rejectInvoiceProof(invoiceId, payload);
            platformInvoices.value = response.items;
            return response.items;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Could not reject invoice proof";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function submitManualInvoiceProof(orgId, invoiceId, payload) {
        isSaving.value = true;
        error.value = null;
        try {
            const response = await billingRepository.submitManualInvoiceProof(orgId, invoiceId, payload);
            tenantInvoices.value = response.items;
            return response.items;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Could not submit invoice proof";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function voidInvoice(invoiceId, payload) {
        isSaving.value = true;
        error.value = null;
        try {
            const subscription = await billingRepository.voidInvoice(invoiceId, payload);
            selectedSubscription.value = subscription;
            upsertPlatformSubscription(subscription);
            return subscription;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Could not void invoice";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function mutateProviderSubscription(mutate) {
        isSaving.value = true;
        error.value = null;
        try {
            const subscription = await mutate();
            selectedSubscription.value = subscription;
            upsertPlatformSubscription(subscription);
            return subscription;
        }
        catch (err) {
            error.value =
                err instanceof Error
                    ? err.message
                    : "Could not update provider subscription";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    function upsertPlatformSubscription(subscription) {
        const exists = platformSubscriptions.value.some((item) => item.organization.id === subscription.organization.id);
        platformSubscriptions.value = exists
            ? platformSubscriptions.value.map((item) => item.organization.id === subscription.organization.id
                ? subscription
                : item)
            : [subscription, ...platformSubscriptions.value];
    }
    function $reset() {
        platformSubscriptions.value = [];
        pagination.value = { ...DEFAULT_PAGINATION };
        plans.value = [];
        selectedSubscription.value = null;
        tenantSummary.value = null;
        platformInvoices.value = [];
        tenantInvoices.value = [];
        latestPreview.value = null;
        isLoading.value = false;
        isLoadingPlans.value = false;
        isLoadingDetail.value = false;
        isSaving.value = false;
        error.value = null;
    }
    return {
        platformSubscriptions,
        pagination,
        plans,
        selectedSubscription,
        tenantSummary,
        platformInvoices,
        tenantInvoices,
        latestPreview,
        isLoading,
        isLoadingPlans,
        isLoadingDetail,
        isSaving,
        error,
        totalAmountDueMinor,
        expectedMonthlyChargeMinor,
        manualCount,
        attentionCount,
        readyToInvoiceCount,
        fetchPlatformSubscriptions,
        fetchPlans,
        createPlan,
        updatePlan,
        createPlanPriceVersion,
        syncPlanPriceToProvider,
        fetchPlatformDetail,
        fetchTenantSummary,
        fetchPlatformInvoices,
        fetchTenantInvoices,
        previewCharge,
        syncSeats,
        updateSubscription,
        migrateToProvider,
        syncProviderSubscription,
        syncProviderSeats,
        changeProviderPlan,
        cancelProviderSubscription,
        createProviderSetupIntent,
        createManualInvoice,
        markInvoicePaid,
        rejectInvoiceProof,
        submitManualInvoiceProof,
        voidInvoice,
        $reset,
    };
});
